import {
  DWELL_SYNC_MS,
  MIN_SAMPLES_VALID,
  PAIR_MAX_ENTER,
  PAIR_MAX_EXIT,
  U_SELF_CAP_ENTER,
  U_SELF_CAP_EXIT,
  U_SELF_MAX_ENTER,
  U_SELF_MAX_EXIT,
} from '../constants.js';

export type SyncSub = 'INVALID' | 'DEGRADED' | 'GOOD';
export type SyncReason = 'SYNC_INVALID' | 'SYNC_SELF' | 'SYNC_PAIR' | 'SYNC_CAP' | 'SYNC_PEER_UNKNOWN';

export interface PairView {
  known: boolean;
  bound: number | null;
  /** server-side hysteresis already applied (§7.2); null when unknown */
  ok: boolean | null;
}

export interface SyncInputs {
  /** U_self; null while invalid */
  uSelf: number | null;
  samplesSinceInvalidation: number;
  counterpartExpected: boolean;
  /** the latest sync.quality pair block; null if none received */
  pair: PairView | null;
}

export interface SyncTrack {
  sub: SyncSub;
  reasons: SyncReason[];
  goodSince: number | null;
  capLatched: boolean;
  selfLatched: boolean;
  pairLatched: boolean;
}

export const initialSyncTrack: SyncTrack = { sub: 'INVALID', reasons: ['SYNC_INVALID'], goodSince: null, capLatched: false, selfLatched: false, pairLatched: false };

export function stepSync(prev: SyncTrack, i: SyncInputs, nowPerf: number): SyncTrack {
  if (i.uSelf === null) {
    return { ...prev, sub: 'INVALID', reasons: ['SYNC_INVALID'], goodSince: null };
  }
  const reasons: SyncReason[] = [];
  const capLatched = prev.capLatched ? i.uSelf > U_SELF_CAP_EXIT : i.uSelf > U_SELF_CAP_ENTER;
  if (capLatched) reasons.push('SYNC_CAP');

  let selfLatched = false;
  let pairLatched = prev.pairLatched;
  if (i.counterpartExpected) {
    if (!i.pair || !i.pair.known || i.pair.bound === null) {
      reasons.push('SYNC_PEER_UNKNOWN');
    } else {
      const b = i.pair.bound;
      pairLatched = i.pair.ok !== null ? !i.pair.ok : prev.pairLatched ? b > PAIR_MAX_EXIT : b >= PAIR_MAX_ENTER;
      if (pairLatched) reasons.push('SYNC_PAIR');
    }
  } else {
    selfLatched = prev.selfLatched ? i.uSelf > U_SELF_MAX_EXIT : i.uSelf > U_SELF_MAX_ENTER;
    if (selfLatched) reasons.push('SYNC_SELF');
  }

  if (reasons.length > 0) {
    return { sub: 'DEGRADED', reasons, goodSince: null, capLatched, selfLatched, pairLatched };
  }
  const goodSince = prev.goodSince ?? nowPerf;
  const held = nowPerf - goodSince >= DWELL_SYNC_MS && i.samplesSinceInvalidation >= MIN_SAMPLES_VALID;
  return { sub: held ? 'GOOD' : 'DEGRADED', reasons: [], goodSince, capLatched, selfLatched, pairLatched };
}
